import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

import { fetchAbridgedOrganizations, fetchAbridgedTranches } from 'api';
import { useSnackContext } from 'hooks/useSnackContext';
import { OrganizationProps, TrancheProps, Option } from 'types';

export type CandidateAdminContextContextProps = {
  organizationOptionArray: Array<Option>;
  organizationArray: Array<OrganizationProps>;
  organizations: Record<string, OrganizationProps>;
  trancheArray: Array<TrancheProps>;
  tranches: Record<number, TrancheProps>;
  trancheOptionArray: Array<Option>;
};

export const CandidateAdminContext =
  createContext<CandidateAdminContextContextProps>(undefined!);
export const ReviewedCandidateProvider = (props: { children: ReactNode }) => {
  const [organizationArray, setOrganizationArray] = useState<
    Array<OrganizationProps>
  >([]);

  const [organizationOptionArray, setOrganizationOptionArray] = useState<
    Array<Option>
  >([]);
  const [trancheOptionArray, setTrancheOptionArray] = useState<Array<Option>>(
    [],
  );
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [organizations, setOrganizations] = useState<
    Record<string, OrganizationProps>
  >({});

  const [trancheArray, setTrancheArray] = useState<Array<TrancheProps>>([]);

  const [tranchesLoaded, setTranchesLoaded] = useState(false);
  const [tranches, setTranches] = useState<Record<string, TrancheProps>>({});

  const { setSnack } = useSnackContext();

  const loadData = useCallback(async () => {
    try {
      const { data: orgArr } = await fetchAbridgedOrganizations({
        params: {
          limit: 99999999,
        },
      });
      setOrganizationArray(orgArr);
      setOrganizations(Object.fromEntries(orgArr.map((o) => [o.id, o])));
      setOrganizationsLoaded(true);
      setOrganizationOptionArray(
        orgArr.map((org) => ({ label: org.name, value: org.id })),
      );
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Fetch Organizations',
        error: e as Error,
      }));
      setOrganizationArray([]);
      setOrganizationsLoaded(false);
    }

    try {
      const { data: trancheArr } = await fetchAbridgedTranches({
        params: {
          limit: 922337203,
        },
      });
      // TODO: use abridged version
      setTrancheArray(trancheArr);
      setTranches(Object.fromEntries(trancheArr.map((t) => [t.id, t])));
      setTrancheOptionArray(
        trancheArr.map((tranche) => {
          return {
            label: `Tranche ${tranche.sequence_no}`,
            value: tranche.id,
            subLabel:
              tranche.status === 'sent_to_state' ? tranche?.sent || '' : '',
          };
        }),
      );
      setTranchesLoaded(true);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Fetch Tranches',
        error: e as Error,
      }));
      setTrancheArray([]);
      setTranchesLoaded(false);
    }
  }, [setSnack]);

  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, 60 * 60 * 1000);
    return () => {
      setOrganizationsLoaded(false);
      setTranchesLoaded(false);
      clearInterval(intervalId);
    };
  }, [loadData]);

  const memoizedReturn = useMemo(
    () => ({
      loadData,
      organizationOptionArray,
      organizationsLoaded,
      organizationArray,
      organizations,
      trancheOptionArray,
      tranchesLoaded,
      trancheArray,
      tranches,
    }),
    [
      loadData,
      organizationOptionArray,
      organizationsLoaded,
      organizationArray,
      organizations,
      trancheOptionArray,
      tranchesLoaded,
      trancheArray,
      tranches,
    ],
  );

  return (
    <CandidateAdminContext.Provider value={memoizedReturn}>
      {props.children}
    </CandidateAdminContext.Provider>
  );
};
